import { Component, OnInit, OnChanges, Input, Output, EventEmitter } from '@angular/core';
import { Alert } from 'app/models/dto/alert.model';
import { BankAccount, BankStatementMonthlySummary } from 'app/models/dto/bank-account.model';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { Document, DocumentLinkInfo } from 'app/models/dto/document.model';
import { LocaleSettingService } from 'app/services/i18n/locale-setting.service';
import { FormatBankValuesService, RevenueSummaryDataFormatted } from '../format-bank-values.service';

export interface MonthInfo {
  date: string;
  year: string;
  month: string;
  id?: number;
  docId?: number;
}

export interface AttributeRow {
  [index: number]: SummaryItem;
}

export interface SummaryItem {
  value: number;
  changeValue: number;
  document: Document;
}

@Component({
  selector: 'app-monthly-totals-account-table',
  templateUrl: './monthly-totals-account-table.component.html',
  styleUrls: ['./monthly-totals-account-table.component.scss'],
  standalone: false
})
export class MonthlyTotalsAccountTableComponent implements OnInit, OnChanges {
  @Input() bankAccount: BankAccount;
  @Input() editAccount: boolean;
  @Input() borrowerId: number;
  @Input() retreiveMonthlySummaries: boolean;
  @Input() originalAccount: BankAccount;
  @Input() showOriginalAccount: boolean;
  @Input() documentLinkInfo: DocumentLinkInfo;
  @Input() newFdTableView?: boolean = false;
  @Input() bankValues: BorrowerValue[]; // FD view only
  @Output() editAccountOrSummaries = new EventEmitter<BankStatementMonthlySummary[]>();
  @Output() createMonthlySummary = new EventEmitter<BankStatementMonthlySummary[]>();
  @Output() showOtherAccount = new EventEmitter<BankAccount | null>();
  @Output() documentError = new EventEmitter<Alert>();

  displayedColumns: string[];
  dataSource: AttributeRow[];
  showTable = false;
  monthDisplayColumns: MonthInfo[] = [];
  displayedColumnObjects: MonthInfo[];
  mostRecentSixMonthlySummaries: BankStatementMonthlySummary[] = [];
  currencyCode: string | null;
  currencySymbol: string | null;
  currentAcctView = true;
  formattedBankValues: RevenueSummaryDataFormatted[]; // FD view only

  months = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
  summaryAttributes = ['docId', 'totaledSales', 'numberOfSalesDeposits', 'totaledCredits',
    'numberOfCredits', 'daysNegBalance', 'batchDays', 'nsfTransactions', 'dailyAvgBalance', 'endingBalance'];
  currencyAttributes = ['totaledSales', 'totaledCredits', 'dailyAvgBalance', 'endingBalance'];
  numberAttributes = ['numberOfSalesDeposits', 'numberOfCredits', 'daysNegBalance', 'batchDays', 'nsfTransactions'];
  summaryDisplayAttributes = {
    docId: 'Documents',
    totaledSales: 'Sales',
    numberOfSalesDeposits: '# Sales Deposits',
    totaledCredits: 'Deposits',
    numberOfCredits: '# Deposits',
    daysNegBalance: 'Negative Days',
    batchDays: 'Batch Days',
    nsfTransactions: 'NSFs',
    dailyAvgBalance: 'Avg. Balance',
    endingBalance: 'Statement Ending Balance',
  };

  // Funding Desk View Settings
  shortMonths: string [] = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  fdSummaryAttributes: string[] = ['docId', 'totaledSales', 'numberOfSalesDeposits', 'totaledCredits',
    'numberOfCredits', 'dailyAvgBalance', 'endingBalance', 'daysNegBalance', 'nsfTransactions'];
  fdSummaryDisplayAttributes = {
    docId: 'Documents',
    totaledSales: 'Sales Amount',
    numberOfSalesDeposits: 'Sales Count',
    totaledCredits: 'Deposit Amount',
    numberOfCredits: 'Deposit Count',
    dailyAvgBalance: 'Average Balance',
    endingBalance: 'Ending Balance',
    daysNegBalance: 'Negative Days',
    nsfTransactions: 'NSF',
  };
  monthlyToAvgAttributes = {
    totaledSales: 'totalSalesAmtAverage',
    numberOfSalesDeposits: 'salesDepositsCountAvg',
    totaledCredits: 'creditsAmountAvg',
    numberOfCredits: 'creditsCountAvg',
    dailyAvgBalance: 'balanceAvg',
    endingBalance: 'endingStatementBalanceAvg',
    daysNegBalance: 'daysNegativeAvg',
    nsfTransactions: 'nsfTransactionsAvg',
  };

  constructor(
    private localeSetting: LocaleSettingService,
    private formatBankValuesService: FormatBankValuesService,
  ) { }

  ngOnChanges(): void {
    if (this.showOriginalAccount === false) {
      this.currentAcctView = true;
      this.initializeSummaries(this.bankAccount);
    }
    if (this.retreiveMonthlySummaries) {
      this.saveSummaryChanges();
      this.retreiveMonthlySummaries = false;
    }
    this.formattedBankValues = this.formatBankValuesService.getBankValuesFormatted(this.bankValues);
  }

  ngOnInit(): void {
    this.currencyCode = this.localeSetting.getCurrencyCode();
    this.currencySymbol = this.localeSetting.getLocaleCurrencySymbol();

    // dynamic money symbols
    this.summaryDisplayAttributes.totaledSales = `${this.currencySymbol} Sales`;
    this.summaryDisplayAttributes.totaledCredits = `${this.currencySymbol} Deposits`;
    const acct = this.currentAcctView ? this.bankAccount : this.originalAccount;
    this.initializeSummaries(acct);
  }

  initializeSummaries(bankAccount: BankAccount): void {
    // get most recent 6 complete months
    const currentDate = new Date();
    this.monthDisplayColumns = this.getPreviousSixMonths(currentDate);

    // Get most recent 6 months of summaries
    this.mostRecentSixMonthlySummaries = this.matchSummariesToPreviousSixMonths(bankAccount);

    // Create Table Header Row With Months as Column Titles
    this.displayedColumnObjects = this.createTableHeaderFromPreviousSixMonths();
    // Funding Desk View add two columns
    if (this.newFdTableView) {
      this.displayedColumnObjects.push(
        {date: 'avg3months', month: '3', year: ''},
        {date: 'avg6months', month: '6', year: ''},
      );
    }

    // From displayedColumnObjects create flat displayedColumns array for Mat Table to recognize in [matColumnDef]
    this.displayedColumns = this.displayedColumnObjects.map(x => x.date);

    // Transpose summaries for displaying
    if (!this.newFdTableView) {
      this.dataSource = this.summaryAttributes.map((attribute) => this.createAttributeRows(attribute));
    } else {
      this.dataSource = this.fdSummaryAttributes.map((attribute) => this.createAttributeRows(attribute));
    }
    this.showTable = true;
  }

  onDocumentError(alert: Alert): void {
    this.documentError.emit(alert);
  }

  onDocumentChange(document: Document, monthIndex: number): void {
    this.dataSource[0][monthIndex].changeValue = document.id;
    const summary = this.createSummaryRows(monthIndex);
    if (summary.id) {
      this.editAccountOrSummaries.emit([summary]);
    } else {
      delete summary.id;
      summary.bankStatementAccountId = this.bankAccount.id;
      summary.month = Number(this.displayedColumnObjects[monthIndex].month);
      summary.year = Number(this.displayedColumnObjects[monthIndex].year);
      summary.fullMonth = 1;
      this.createMonthlySummary.emit([summary]);
    }
  }

  goToAccount(event) {
    event.preventDefault();
    if (this.currentAcctView) {
      this.showOtherAccount.emit(this.originalAccount);
    } else {
      this.showOtherAccount.emit(null);
    }
    this.currentAcctView = !this.currentAcctView;
    const acct = this.currentAcctView ? this.bankAccount : this.originalAccount;
    this.initializeSummaries(acct);
  }

  viewDocument(documentId: number): void {
    const url = `${this.documentLinkInfo.baseUrl}/document/${documentId}${this.documentLinkInfo.queryParameters}`;
    window.open(url, '_blank');
  }

  isViewOnlyCell(monthIndex: number, attributeIndex: number): boolean {
    return (
      attributeIndex === 0 && monthIndex > 0) ||
      (monthIndex > 0 &&
      ((!this.dataSource[0][monthIndex].value && this.editAccount) || !this.editAccount)
    );
  }

  isAvgColumn(column): boolean {
    return column.date === 'avg3months' || column.date === 'avg6months';
  }

  displayTotalTag(column, element, monthIndex, attributeIndex): boolean {
    return this.isAvgColumn(column) && element[monthIndex]?.value &&
      (
        this.fdSummaryAttributes[attributeIndex] === 'nsfTransactions' ||
        this.fdSummaryAttributes[attributeIndex] === 'daysNegBalance'
      );
  }

  roundedAvg(n: number): number | undefined {
    if (!n) { return n; }
    return Math.round(n * 100) / 100 ?? null;
  }

  private createTableHeaderFromPreviousSixMonths(): MonthInfo[] {
    const tableCornerPlaceholder = [{ date: '0', year: '', month: '' }];
    return tableCornerPlaceholder.concat(this.mostRecentSixMonthlySummaries.map((s, i) => s.year ?
        {
          date: `${s.year}-${s.month}`,
          month: s.month.toString(),
          year: s.year.toString(),
          id: s.id,
          docId: s.docId
        } :
        this.monthDisplayColumns[i]));
  }

  private matchSummariesToPreviousSixMonths(bankAccount: BankAccount): BankStatementMonthlySummary[] {
    const matchedSummaries: BankStatementMonthlySummary[] = [];
    this.monthDisplayColumns.forEach((month) => {
      const matchedSummary = bankAccount.bank_statement_monthly_summaries?.find((summary) =>
      `${summary.year}-${summary.month}` === `${month.date}`);
      matchedSummaries.push(matchedSummary ? matchedSummary : {} as BankStatementMonthlySummary);
    });
    return matchedSummaries;
  }

  private getPreviousSixMonths(currentDate: Date): MonthInfo[] {
    const monthDisplayColumns: MonthInfo[] = [];
    [...Array(6)].forEach(() => {
      currentDate.setDate(0);
      const month = currentDate.getUTCMonth() + 1;
      const year = currentDate.getUTCFullYear();
      monthDisplayColumns.push({
        date: `${year}-${month}`,
        month: month.toString(),
        year: year.toString()
      });
    });
    return monthDisplayColumns;
  }

  private createAttributeRows(attribute) {
    const output = {};

    output[0] = { value: attribute };
    this.mostRecentSixMonthlySummaries.forEach((summary, i) => {
      output[i + 1] = { value: summary[attribute], changeValue: summary[attribute] };
      if (attribute === 'docId') {
        output[i + 1].documents = summary['sourceDocuments']?.length ? summary['sourceDocuments'].map((id) => ({ id } as Document)) : [];
      }
    });

    if (this.newFdTableView) {
      const avgAttribute = this.monthlyToAvgAttributes[attribute];
      // add3monthaverage here
      output[7] = {
        value: this.formattedBankValues[0][avgAttribute] ?? undefined,
        changeValue: this.formattedBankValues[0][avgAttribute] ?? undefined
      };
      // add6monthaverage here
      output[8] = {
        value: this.formattedBankValues[1][avgAttribute] ?? undefined,
        changeValue: this.formattedBankValues[1][avgAttribute] ?? undefined
      };
    }

    return output;
  }

  private saveSummaryChanges() {
    const summariesToUpdate: BankStatementMonthlySummary[] = [];
    // For Each of Six Months of Summaries
    for (let i = 1; i <= 6; i++) {
      if (!this.dataSource[0][i].value) {
        continue;
      }
      const summary = this.createSummaryRows(i);
      if (Object.keys(summary ?? {}).length) {
        summariesToUpdate.push(summary);
      }
    }
    this.editAccountOrSummaries.emit(summariesToUpdate);
  }

  private createSummaryRows(monthIndex: number): BankStatementMonthlySummary {
    const modifiedSummaryData: BankStatementMonthlySummary = {} as BankStatementMonthlySummary;
    this.dataSource.forEach((attributeRow) => {
      if (attributeRow[monthIndex].value !== attributeRow[monthIndex].changeValue) {
        modifiedSummaryData[attributeRow[0].value] = attributeRow[monthIndex].changeValue;
        modifiedSummaryData.id = this.displayedColumnObjects[monthIndex].id;
      }
    });
    return modifiedSummaryData;
  }

}
